import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import {
  FaPlus, FaCheckCircle, FaUserAlt, FaLaptop,
  FaRegFileAlt, FaWhatsapp, FaTimes, FaLayerGroup,
  FaUsers, FaUserCheck, FaMoneyBillWave, FaBullseye,
  FaSearch, FaSyncAlt, FaAngleDown, FaChartPie
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';

/* --------------------------------- APIs ---------------------------------- */
const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';
// Explicit limit: the endpoint defaults to 4000 rows with none passed,
// silently truncating the real roster (4085+ members).
const API_MEMBERS      = 'https://dev.sliitleo.org/Backend/member_api.php?action=list_basic&limit=40000';

/* -------------------------------- Config --------------------------------- */
const POLL_MS_VISIBLE = 5000;
const POLL_MS_HIDDEN  = 15000;
const TARGET = 500; // top presentation bar target

/* -------------------------------- Helpers -------------------------------- */
function pickInstallations(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.rows && Array.isArray(payload.rows)) return payload.rows;
  if (payload?.all && Array.isArray(payload.all)) return payload.all;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.installations && Array.isArray(payload.installations)) return payload.installations;
  return [];
}
async function toJsonSafe(res) {
  const text = await res.text();
  try { return JSON.parse(text); } catch { throw new Error('Invalid JSON from server'); }
}
async function postForm(url, params) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    cache: 'no-store',
    body: params.toString(),
  });
  const raw = await res.text();
  let json; try { json = JSON.parse(raw); } catch { json = null; }
  return { ok: res.ok, status: res.status, raw, json };
}
function toWaPhone(v = '') {
  const digits = String(v).replace(/\D+/g, '');
  if (!digits) return '';
  if (digits.startsWith('94')) return digits;
  return '94' + digits.replace(/^0/, '');
}
function extractIdNameRole(m = {}) {
  const id = String(m.SLIITID ?? m.SLIITLeoID ?? m.SLIIT_Leo_ID ?? m.sliitid ?? m.sliitID ?? '').trim();
  const name = String(m.FullName ?? m.full_name ?? m.FullName ?? m.fullName ?? m.Name ?? m.name ?? '').trim();
  const role = String(m.Role ?? m.role ?? m.userRole ?? m.UserRole ?? '').trim();
  return { id, name, role };
}
function isVerifiedMember(i){ return i.Verify === 1 || i.Verify === '1' || !!i.VerifiedDate; }
function getRegDate(i){ return i.RegDate || i.RegisteredDate || i.CreatedAt || i.CreatedDate || i.created_at || ''; }
function fmtDate(d){ try { return d ? new Date(d).toLocaleDateString() : ''; } catch { return ''; } }

/* ---------- participation + cash ---------- */
function getParticipationFlag(i){
  // Uses ONLY the PartStatus column (as you requested)
  const v = String(i.PartStatus ?? '').trim().toLowerCase();
  if (!v) return null;
  if (['1','yes','true','y','attend','attending','participate','participating','will attend','confirmed'].includes(v)) return true;
  if (['0','no','false','n','not attend','not attending','not participating','not participate','decline','declined','cancelled'].includes(v)) return false;
  if (v.includes('not') && (v.includes('attend') || v.includes('particip'))) return false;
  if (v.includes('attend') || v.includes('particip')) return true;
  return null;
}
function isCashInHand(i){
  const raw = String(
    i.PaymentMethod ?? i.PayMethod ?? i.PaymentType ?? i.Method ?? i.Bank ?? ''
  ).toLowerCase().trim();
  if (!raw) return false;
  return raw === 'cash' || raw === 'cash in hand' || raw === 'cash-in-hand' || raw === 'cash hand' || raw.includes('cash');
}

/* -------------------------- Filter chip definitions ------------------------ */
const SEGMENTS = [
  { key: 'all',        label: 'All',            countKey: 'total' },
  { key: 'verified',   label: 'Verified',       countKey: 'verified' },
  { key: 'unverified', label: 'Unverified',     countKey: 'unverified' },
  { key: 'checked',    label: 'Checked-in',     countKey: 'checked' },
  { key: 'notchecked', label: 'Not Checked-in', countKey: 'notchecked' },
  { key: 'cash',       label: 'Cash-in-hand',   countKey: 'cashCount' },
];

/* ================================ PAGE =================================== */
export default function Installation() {
  const user = JSON.parse(localStorage.getItem('user')) || {
    SLIITID: '',
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png'
  };

  const [verifyPopup, setVerifyPopup] = useState({ open: false, regid: null });
  const [reportModal, setReportModal] = useState({ open: false, regid: null, reason: '', loading: false, error: '' });

  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('all'); // all | verified | unverified | checked | notchecked | cash
  const [sort, setSort] = useState('recent');   // recent | oldest | name

  const [rawInstallations, setRawInstallations] = useState([]);
  const [idToMeta, setIdToMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // UX: show “new items” toast when fresh records appear
  const [newCountToast, setNewCountToast] = useState(0);
  const lastSigRef = useRef('');
  const pollTimer = useRef(null);
  const currentPollAbort = useRef(null);

  /* ----------------------------- Boot & Poll ----------------------------- */
  const loadMemberDirectory = async () => {
    try {
      const res = await fetch(API_MEMBERS, { method: 'GET', cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const map = {};
      for (const m of arr) {
        const { id, name, role } = extractIdNameRole(m);
        if (id) map[id] = { name: name || '', role: role || '' };
      }
      setIdToMeta(map);
    } catch { setIdToMeta({}); }
  };

  const signature = (arr) => {
    try {
      const compact = arr.map(r => `${r.RegID ?? ''}|${r.SLIITID ?? ''}|${r.UpdatedAt ?? r.VerifiedDate ?? r.CheckedDate ?? r.RegDate ?? ''}`).sort().join('~');
      let hash = 0;
      for (let i=0;i<compact.length;i++) hash = (hash*31 + compact.charCodeAt(i))|0;
      return String(hash);
    } catch { return ''+Date.now(); }
  };

  const loadInstallations = async (signal) => {
    setErr('');
    try {
      // add a small cache-buster tied to last update time
      const url = API_INSTALLATION + (API_INSTALLATION.includes('?') ? '&' : '?') + 't=' + Date.now();
      const res = await fetch(url, { method: 'GET', cache: 'no-store', signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      const next = pickInstallations(data);
      const nextSig = signature(next);
      const prevSig = lastSigRef.current;

      // update state only if data changed (prevents flicker)
      if (nextSig !== prevSig) {
        const prevLen = rawInstallations.length;
        setRawInstallations(next);
        setLastUpdated(new Date());
        lastSigRef.current = nextSig;

        const delta = Math.max(0, next.length - prevLen);
        if (delta > 0) {
          setNewCountToast(delta);
          // auto-hide toast after a bit
          window.clearTimeout((loadInstallations._toastTO));
          loadInstallations._toastTO = window.setTimeout(()=>setNewCountToast(0), 3500);
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErr('Could not load registrations. Tap reload to try again.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadMemberDirectory(), loadInstallations()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function schedulePoll(ms) {
      clearInterval(pollTimer.current);
      pollTimer.current = setInterval(() => {
        if (currentPollAbort.current) currentPollAbort.current.abort();
        const ctrl = new AbortController();
        currentPollAbort.current = ctrl;
        loadInstallations(ctrl.signal);
      }, ms);
    }
    schedulePoll(POLL_MS_VISIBLE);
    const onVis = () => {
      schedulePoll(document.visibilityState === 'visible' ? POLL_MS_VISIBLE : POLL_MS_HIDDEN);
      if (document.visibilityState === 'visible') {
        if (currentPollAbort.current) currentPollAbort.current.abort();
        const ctrl = new AbortController();
        currentPollAbort.current = ctrl;
        loadInstallations(ctrl.signal);
      }
    };
    const onFocus = () => {
      if (currentPollAbort.current) currentPollAbort.current.abort();
      const ctrl = new AbortController();
      currentPollAbort.current = ctrl;
      loadInstallations(ctrl.signal);
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      clearInterval(pollTimer.current);
      if (currentPollAbort.current) currentPollAbort.current.abort();
    };
  }, []);

  /* --------------------------- Derivations ------------------------------- */
  const installations = useMemo(() => rawInstallations.map(r => ({
    ...r,
    VerifierName: r.VerifierName || r.VerifiedByName || (r.VerifiedBy ? (idToMeta[String(r.VerifiedBy)]?.name || '') : '') || '',
    CheckerName : r.CheckerName  || r.CheckedByName  || (r.CheckedBy  ? (idToMeta[String(r.CheckedBy )]?.name || '') : '') || ''
  })), [rawInstallations, idToMeta]);

  const currentUserRole = useMemo(() => {
    const meta = idToMeta[String(user.SLIITID)] || {};
    return (meta.role || user.role || '').trim();
  }, [idToMeta, user.SLIITID, user.role]);

  // Only System Admin can verify
  const isSystemAdmin = useMemo(() => (currentUserRole || '').toLowerCase() === 'system admin', [currentUserRole]);
  const isAnyAdmin = useMemo(() => ['system admin','admin','administrator'].includes((currentUserRole||'').toLowerCase()), [currentUserRole]);

  const totals = useMemo(() => {
    const total = installations.length;
    const checked = installations.filter(i => i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true).length;
    const verified = installations.filter(isVerifiedMember).length;
    const unverified = total - verified;
    const notchecked = total - checked;

    let participate = 0, notParticipate = 0;
    let cashCount = 0;
    const food = installations.reduce((acc, item) => {
      const raw = String(item.FoodPref || '').trim().toLowerCase();
      if (/^veg(?:etarian)?$/.test(raw) || raw === 'vegan') acc.veg++;
      else if (/^non[\s-]?veg(?:etarian)?$/.test(raw) || raw === 'non') acc.non++;
      else if (raw) acc.other++; else acc.unset++;
      return acc;
    }, { veg:0, non:0, other:0, unset:0 });

    for (const it of installations){
      const pf = getParticipationFlag(it);
      if (pf === true) participate++;
      else if (pf === false) notParticipate++;
      if (isCashInHand(it)) cashCount++;
    }

    return { total, checked, verified, unverified, notchecked, food, participate, notParticipate, cashCount };
  }, [installations]);

  const pctToTarget = TARGET ? Math.min(100, Math.round((totals.total / TARGET) * 100)) : 0;

  const filtered = useMemo(() => {
    let list = [...installations];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        String(i.Name || '').toLowerCase().includes(q) ||
        String(i.SLIITID || '').toLowerCase().includes(q)
      );
    }
    list = list.filter(i => {
      if (segment === 'verified')   return isVerifiedMember(i);
      if (segment === 'unverified') return !isVerifiedMember(i);
      if (segment === 'checked')    return i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true;
      if (segment === 'notchecked') return !(i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true);
      if (segment === 'cash')       return isCashInHand(i);
      return true;
    });
    list.sort((a,b) => {
      if (sort === 'name')   return String(a.Name||'').localeCompare(String(b.Name||''));
      const da = new Date(getRegDate(a) || a.created_at || 0).getTime();
      const db = new Date(getRegDate(b) || b.created_at || 0).getTime();
      return sort === 'oldest' ? (da - db) : (db - da);
    });
    return list;
  }, [installations, search, segment, sort]);

  /* ------------------------------ Actions -------------------------------- */
  const handleVerify = (regid) => { if (isSystemAdmin) setVerifyPopup({ open:true, regid }); };
  const handleVerifyCancel = () => setVerifyPopup({ open:false, regid:null });

  const handleVerifyAccept = async () => {
    if (!isSystemAdmin) return;
    const regid = verifyPopup.regid;
    setVerifyPopup({ open:false, regid:null });
    try {
      const params = new URLSearchParams();
      params.set('action','verify');
      params.set('RegID', String(regid));
      params.set('Verify','1');
      params.set('VerifiedBy', String(user.SLIITID || ''));
      params.set('VerifiedByName', String(user.name || 'System Admin'));
      params.set('SendEmail','1');
      params.set('EmailType','verified');
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        Verify: 1,
        VerifiedBy: user.SLIITID,
        VerifierName: json.VerifierName || r.VerifierName || user.name || '',
        VerifiedDate: json.VerifiedDate || r.VerifiedDate || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to verify');
    }
  };

  const handleCheckIn = async (regid, currentStatus) => {
    if (!isAnyAdmin) return;
    if (currentStatus === 1 || currentStatus === '1' || currentStatus === true) return;
    try {
      const params = new URLSearchParams();
      params.set('action','checkin');
      params.set('RegID', String(regid));
      params.set('CheckIn','1');
      params.set('CheckedBy', String(user.SLIITID || ''));
      params.set('CheckedByName', String(user.name || 'Admin'));
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        CheckIn: 1,
        CheckedBy: user.SLIITID,
        CheckerName: json.CheckerName || r.CheckerName || user.name || '',
        CheckedDate: json.CheckedDate || r.CheckedDate || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to check-in');
    }
  };

  const handleReportSubmit = async () => {
    if (!isAnyAdmin) { setReportModal(prev => ({ ...prev, error: 'Only admins can report registrations.' })); return; }
    if (!reportModal.reason.trim()) { setReportModal(prev => ({ ...prev, error: 'Please enter a reason' })); return; }
    try {
      setReportModal(prev => ({ ...prev, loading:true, error:'' }));
      const formData = new FormData();
      formData.append('action','report');
      formData.append('RegID', reportModal.regid);
      formData.append('ReportReason', reportModal.reason);
      formData.append('ReportedBy', user.SLIITID || 'admin');
      formData.append('ReportedDate', new Date().toISOString().slice(0,19).replace('T',' '));
      const response = await axios.post(API_INSTALLATION, formData, { headers:{ 'Content-Type':'multipart/form-data' } });
      if (!response.data?.success) throw new Error(response.data?.error || 'Failed to submit');
      setRawInstallations(list => list.map(r => r.RegID === reportModal.regid ? { ...r, ReportReason: reportModal.reason } : r));
      setReportModal({ open:false, regid:null, reason:'', loading:false, error:'' });
    } catch (e) {
      setReportModal(prev => ({ ...prev, error: e.message || 'Failed', loading:false }));
    }
  };

  /* ------------------------------- UI ------------------------------------ */
  if (loading) {
    return (
      <>
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 px-4 sm:px-6 py-6 sm:py-8">
          <div className="page-head">
            <div>
              <h1><FaLaptop style={{ marginRight: 10, color: 'var(--accent)' }} />Installation</h1>
              <p className="page-desc">Annual installation ceremony — registration &amp; check-in</p>
            </div>
          </div>

          <div className="priority">
            <div className="panel hero-metric">
              <div className="skeleton" style={{ height: 16, width: 130 }} />
              <div className="skeleton" style={{ height: 36, width: 90 }} />
              <div className="skeleton" style={{ height: 12, width: 160 }} />
            </div>
            <div className="panel tile">
              <div className="skeleton" style={{ height: 14, width: 80 }} />
              <div className="skeleton" style={{ height: 26, width: 50 }} />
            </div>
            <div className="panel tile">
              <div className="skeleton" style={{ height: 14, width: 80 }} />
              <div className="skeleton" style={{ height: 26, width: 50 }} />
            </div>
          </div>

          <div className="panel" style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 52, borderRadius: 'var(--radius-sm)' }} />
            ))}
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      {/* Verify modal (System Admin only) */}
      {verifyPopup.open && isSystemAdmin && (
        <div className="modal-overlay">
          <div className="modal-panel panel">
            <div className="modal-head">
              <h3>Verify Registration</h3>
              <button onClick={handleVerifyCancel} className="icon-btn" aria-label="Close">
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px',
                  display: 'grid', placeItems: 'center',
                  background: 'rgba(63,174,106,.14)', color: 'var(--success)',
                }}
              >
                <FaCheckCircle size={22} />
              </div>
              <p style={{ color: 'var(--text-2)', fontSize: 13.5 }}>
                Confirm you want to <strong style={{ color: 'var(--text-1)' }}>verify</strong> this registration.
              </p>
            </div>
            <div className="modal-foot">
              <button onClick={handleVerifyCancel} className="btn btn-ghost">Cancel</button>
              <button onClick={handleVerifyAccept} className="btn btn-primary">Accept</button>
            </div>
          </div>
        </div>
      )}

      {/* Report modal (admins) */}
      {reportModal.open && isAnyAdmin && (
        <div className="modal-overlay">
          <div className="modal-panel panel lg">
            <div className="modal-head">
              <h3>Report Issue</h3>
              <button
                onClick={() => setReportModal(prev => ({ ...prev, open: false }))}
                className="icon-btn"
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="field-label" htmlFor="inst-report-reason">
                  Reason <span className="req">*</span>
                </label>
                <textarea
                  id="inst-report-reason"
                  rows={4}
                  value={reportModal.reason}
                  onChange={(e) => setReportModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Add details..."
                  className={reportModal.error ? 'invalid' : ''}
                />
                {reportModal.error && <div className="field-error">{reportModal.error}</div>}
              </div>
            </div>
            <div className="modal-foot">
              <button
                type="button"
                onClick={() => setReportModal(prev => ({ ...prev, open: false }))}
                className="btn btn-ghost"
                disabled={reportModal.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReportSubmit}
                className="btn btn-danger"
                disabled={reportModal.loading}
              >
                {reportModal.loading && <span className="spinner" style={{ marginRight: 6 }} />}
                {reportModal.loading ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="lg:ml-64 mt-14 px-4 sm:px-6 py-6 sm:py-8">
        {/* tiny toast for new data */}
        {newCountToast > 0 && (
          <div
            className="success-banner"
            style={{ width: 'fit-content', margin: '0 auto var(--sp-4)', position: 'sticky', top: 8, zIndex: 30 }}
          >
            <FaCheckCircle />
            <span>+{newCountToast} new record{newCountToast > 1 ? 's' : ''} loaded</span>
          </div>
        )}

        <div className="page-head">
          <div>
            <h1><FaLaptop style={{ marginRight: 10, color: 'var(--accent)' }} />Installation</h1>
            <p className="page-desc">Annual installation ceremony — registration &amp; check-in</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/add-registration" className="btn btn-primary" title="Add a new registration">
              <FaPlus /> Add Registration
            </Link>
            <Link to="/installation-report" className="btn btn-ghost" title="Open summary dashboard">
              <FaLayerGroup /> Summary
            </Link>
          </div>
        </div>

        <StatsOverview totals={totals} target={TARGET} pct={pctToTarget} lastUpdated={lastUpdated} />

        <ChartErrorBoundary>
          <AnalyticsPanel totals={totals} />
        </ChartErrorBoundary>

        <div className="panel" style={{ marginBottom: 'var(--sp-6)' }}>
          <div className="section-head" style={{ flexWrap: 'wrap', gap: 10 }}>
            <h2>Registrants</h2>
            <div className="filter-row" style={{ flexWrap: 'wrap' }}>
              {SEGMENTS.map(s => (
                <button
                  key={s.key}
                  type="button"
                  className={`chip${segment === s.key ? ' active' : ''}`}
                  onClick={() => setSegment(s.key)}
                >
                  {s.label} <span className="num">{totals[s.countKey]}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '0 var(--sp-4) var(--sp-4)' }}>
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
              <FaSearch
                aria-hidden="true"
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 13 }}
              />
              <input
                type="text"
                placeholder="Search by name or SLIIT ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search registrations"
                style={{ paddingLeft: 32 }}
              />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort" style={{ width: 'auto' }}>
              <option value="recent">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A→Z</option>
            </select>
            <button
              type="button"
              onClick={async () => { setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }}
              className="btn btn-ghost btn-sm"
            >
              <FaSyncAlt size={12} /> Reload
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaUsers /></div>
              <h3>No registrations found</h3>
              <p>{err || 'Try a different search or filter.'}</p>
              {err && (
                <button
                  onClick={async () => { setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }}
                  className="btn btn-primary btn-sm"
                >
                  <FaSyncAlt size={11} /> Reload
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Registrant</th>
                    <th>Registered</th>
                    <th>Payment</th>
                    <th>Participation</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(item => (
                    <Row
                      key={item.RegID}
                      item={item}
                      isSystemAdmin={isSystemAdmin}
                      isAnyAdmin={isAnyAdmin}
                      onVerify={() => handleVerify(item.RegID)}
                      onReport={() => setReportModal({ open: true, regid: item.RegID, reason: '', loading: false, error: '' })}
                      onCheckIn={() => handleCheckIn(item.RegID, item.CheckIn)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

/* ============================ Small Components =========================== */

function StatsOverview({ totals, target, pct, lastUpdated }) {
  const { food } = totals;
  return (
    <>
      <div className="priority">
        <div className="panel hero-metric">
          <div className="hero-top">
            <span className="eyebrow">Total Registered</span>
            <div className="ring" style={{ '--pct': pct }} data-pct={`${pct}%`} />
          </div>
          <div className="hero-value num">{totals.total}</div>
          <div className="hero-sub">
            <FaBullseye style={{ marginRight: 6 }} />
            {pct}% of {target} target
            {lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString()}` : ''}
          </div>
          <div className="hero-list">
            <div className="hero-list-row"><span className="name">Veg</span><span className="when num">{food.veg}</span></div>
            <div className="hero-list-row"><span className="name">Non-Veg</span><span className="when num">{food.non}</span></div>
            <div className="hero-list-row"><span className="name">Other</span><span className="when num">{food.other}</span></div>
            <div className="hero-list-row"><span className="name">Not set</span><span className="when num">{food.unset}</span></div>
          </div>
        </div>

        <div className="panel tile">
          <div>
            <div className="tile-label"><FaUserCheck style={{ marginRight: 6 }} />Verified</div>
            <div className="tile-value num">{totals.verified}</div>
          </div>
          <div className={`tile-trend ${totals.unverified > 0 ? 'trend-warn' : 'trend-up'}`}>
            {totals.unverified} unverified
          </div>
        </div>

        <div className="panel tile">
          <div>
            <div className="tile-label"><FaCheckCircle style={{ marginRight: 6 }} />Checked-in</div>
            <div className="tile-value num">{totals.checked}</div>
          </div>
          <div className={`tile-trend ${totals.notchecked > 0 ? 'trend-flat' : 'trend-up'}`}>
            {totals.notchecked} remaining
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        <div className="panel tile">
          <div className="tile-label"><FaMoneyBillWave style={{ marginRight: 6 }} />Cash-in-hand</div>
          <div className="tile-value num">{totals.cashCount}</div>
        </div>
        <div className="panel tile">
          <div className="tile-label">Participating</div>
          <div className="tile-value num">{totals.participate}</div>
          <div className="hero-sub">{totals.notParticipate} not participating</div>
        </div>
        <div className="panel tile">
          <div className="tile-label">Unverified</div>
          <div className="tile-value num">{totals.unverified}</div>
        </div>
        <div className="panel tile">
          <div className="tile-label">Not Checked-in</div>
          <div className="tile-value num">{totals.notchecked}</div>
        </div>
      </div>
    </>
  );
}

/* ----------------------------- Analytics charts --------------------------- */
function AnalyticsDonut({ title, data, colors }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{ width: 72, height: 72, flexShrink: 0 }}
          role="img"
          aria-label={`Donut chart of ${title.toLowerCase()}: ${data.map(d => `${d.name} ${d.value}`).join(', ')}.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={20}
                outerRadius={34}
                paddingAngle={total ? 2 : 0}
                stroke="none"
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#1c1c1c', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontSize: 11 }}
                itemStyle={{ color: '#ffffff' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          {data.map((d, i) => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: colors[i % colors.length], flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap' }}>{d.name}</span>
              <span className="num" style={{ marginLeft: 'auto', color: 'var(--text-1)', fontWeight: 700 }}>{d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error('Installation analytics charts failed to render:', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="panel" style={{ marginBottom: 'var(--sp-6)', textAlign: 'center', color: 'var(--text-3)' }}>
          Analytics unavailable in this browser.
        </div>
      );
    }
    return this.props.children;
  }
}

function AnalyticsPanel({ totals }) {
  const { food } = totals;
  const verifiedData = [
    { name: 'Verified', value: totals.verified },
    { name: 'Unverified', value: totals.unverified },
  ];
  const checkinData = [
    { name: 'Checked-in', value: totals.checked },
    { name: 'Not checked-in', value: totals.notchecked },
  ];
  const paymentData = [
    { name: 'Cash-in-hand', value: totals.cashCount },
    { name: 'Other/Bank', value: Math.max(0, totals.total - totals.cashCount) },
  ];
  const foodData = [
    { label: 'Veg', count: food.veg },
    { label: 'Non-Veg', count: food.non },
    { label: 'Other', count: food.other },
    { label: 'Not set', count: food.unset },
  ];

  return (
    <div className="panel" style={{ marginBottom: 'var(--sp-6)' }}>
      <div className="section-head">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaChartPie size={13} style={{ color: 'var(--accent)' }} aria-hidden="true" /> Registration Analytics
        </h2>
      </div>
      <div
        style={{
          padding: '0 var(--sp-3) var(--sp-4)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--sp-3)',
        }}
      >
        <AnalyticsDonut title="Verification status" data={verifiedData} colors={['#3fae6a', '#e2645c']} />
        <AnalyticsDonut title="Check-in status" data={checkinData} colors={['#e11d2e', '#6c7ea3']} />
        <AnalyticsDonut title="Payment method" data={paymentData} colors={['#e11d2e', '#7a0d18']} />

        <div
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3)',
            minWidth: 0,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 6 }}>Food preference</div>
          <div
            style={{ width: '100%', height: 76 }}
            role="img"
            aria-label={`Bar chart of food preference: Veg ${food.veg}, Non-Veg ${food.non}, Other ${food.other}, Not set ${food.unset}.`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={foodData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.10)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#c9c9c9', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.16)' }} tickLine={false} />
                <YAxis tick={{ fill: '#c9c9c9', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#1c1c1c', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontSize: 11 }}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                />
                <Bar dataKey="count" fill="#e11d2e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Memoized row for speed */
const Row = memo(function Row({ item, isSystemAdmin, isAnyAdmin, onVerify, onReport, onCheckIn }) {
  const [open, setOpen] = useState(false);
  const wa = toWaPhone(item.Contact);
  const checked = item.CheckIn === 1 || item.CheckIn === '1' || item.CheckIn === true;
  const verified = isVerifiedMember(item);
  const isReported = !!(item.ReportReason && !/^(null|null|undefined|\s*)$/i.test(String(item.ReportReason)));
  const regDate = fmtDate(getRegDate(item));

  const partFlag = getParticipationFlag(item);
  const partBadge = partFlag === true ? 'Participating' : (partFlag === false ? 'Not Participating' : 'Unknown');
  const isCash = isCashInHand(item);

  const foodLabel = (() => {
    const fp = String(item.FoodPref || '').toLowerCase().trim();
    if (fp === 'veg' || fp === 'vegetarian') return 'Veg';
    if (fp === 'non-veg' || fp === 'nonveg' || fp === 'non vegetarian') return 'Non';
    return fp || 'N/A';
  })();

  return (
    <>
      <tr>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: '999px', flexShrink: 0,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                display: 'grid', placeItems: 'center', color: 'var(--text-2)',
              }}
            >
              <FaUserAlt size={13} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="proj-name truncate" title={item.Name}>{item.Name}</div>
              <div className="proj-cat truncate">
                <span className="num">{item.SLIITID}</span>{item.Faculty ? ` · ${item.Faculty}` : ''}
              </div>
            </div>
          </div>
        </td>

        <td>
          <div className="num">{regDate || '—'}</div>
          <div className="proj-cat">{foodLabel}</div>
        </td>

        <td>
          <div>{item.Amount || '—'}</div>
          {(item.Bank || isCash) && (
            <span className={`pill ${isCash ? 'pill-warning' : 'pill-info'}`} style={{ marginTop: 4 }}>
              <span className="dot" />{isCash ? 'Cash-in-hand' : item.Bank}
            </span>
          )}
        </td>

        <td>
          {partFlag === true && <span className="pill pill-success"><span className="dot" />{partBadge}</span>}
          {partFlag === false && <span className="pill pill-danger"><span className="dot" />{partBadge}</span>}
          {partFlag === null && <span className="pill pill-info"><span className="dot" />{partBadge}</span>}
        </td>

        <td>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {verified
              ? <span className="pill pill-success"><span className="dot" />Verified</span>
              : <span className="pill pill-danger"><span className="dot" />Unverified</span>}
            {checked && <span className="pill pill-success"><span className="dot" />Checked-in</span>}
            {isReported && <span className="pill pill-danger"><span className="dot" />Reported</span>}
          </div>
        </td>

        <td>
          <div className="row-actions" style={{ flexWrap: 'wrap' }}>
            {item.FileURL && (
              <button
                className="icon-btn"
                onClick={() => window.open(item.FileURL, '_blank')}
                title="View payment slip"
                aria-label="View payment slip"
                type="button"
              >
                <FaRegFileAlt size={13} />
              </button>
            )}
            {wa && (
              <a
                href={`https://wa.me/+${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="icon-btn"
                title="Chat on WhatsApp"
                aria-label={`Chat on WhatsApp with ${item.Name || 'registrant'}`}
              >
                <FaWhatsapp size={13} />
              </a>
            )}
            {isSystemAdmin && !verified && !isReported && (
              <button className="btn btn-primary btn-sm" onClick={onVerify}>Verify</button>
            )}
            {isAnyAdmin && verified && !checked && (
              <button className="btn btn-primary btn-sm" onClick={onCheckIn}>Check-in</button>
            )}
            {isAnyAdmin && !isReported && (
              <button className="btn btn-danger btn-sm" onClick={onReport}>Report</button>
            )}
            <button
              className="icon-btn"
              onClick={() => setOpen(v => !v)}
              aria-expanded={open}
              aria-label={open ? 'Hide details' : 'Show details'}
              title={open ? 'Hide details' : 'Show details'}
            >
              <FaAngleDown style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }} size={14} />
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--surface-2)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-2) 0',
              }}
            >
              <Detail label="PartStatus" value={item.PartStatus ?? '-'} />
              <Detail label="Food Pref" value={item.FoodPref ?? '-'} />
              <Detail label="Amount" value={item.Amount ?? '-'} />
              <Detail label="Payment" value={isCash ? 'Cash-in-hand' : (item.Bank ?? item.PaymentMethod ?? '-')} />
              <Detail label="Contact" value={item.Contact ?? '-'} />
              <Detail label="Email" value={item.Email ?? '-'} />
              <Detail label="Verified By" value={item.VerifierName || '-'} />
              <Detail label="Verified Date" value={fmtDate(item.VerifiedDate)} />
              <Detail label="Checked By" value={item.CheckerName || '-'} />
              <Detail label="Checked Date" value={fmtDate(item.CheckedDate)} />
            </div>
            {isReported && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--error)' }}>
                <strong style={{ color: 'var(--text-1)' }}>Reported reason:</strong> {item.ReportReason}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
});

function Detail({ label, value }) {
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-1)', fontWeight: 600 }}>{String(value)}</div>
    </div>
  );
}
